import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../../firebase/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import Header from './Header';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AirtimePrices = () => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  const [discounts, setDiscounts] = useState({
    MTN: { regular: 0, upgraded: 0, api: 0 },
    GLO: { regular: 0, upgraded: 0, api: 0 },
    AIRTEL: { regular: 0, upgraded: 0, api: 0 },
    NINE_MOBILE: { regular: 0, upgraded: 0, api: 0 }
  });
  
  const [originalDiscounts, setOriginalDiscounts] = useState({});

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const fetchDiscounts = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/airtime-discounts`, {
        headers: { 'Authorization': `Bearer ${idToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDiscounts(data);
        setOriginalDiscounts(JSON.parse(JSON.stringify(data)));
        setHasChanges(false);
      }
    } catch (error) {
      console.error("Error fetching airtime discounts:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchDiscounts();
    }
  }, [user, fetchDiscounts]);

  const handleChange = (network, role, value) => {
    const newDiscounts = { ...discounts };
    newDiscounts[network][role] = parseFloat(value) || 0;
    setDiscounts(newDiscounts);
    
    // Check if changed
    setHasChanges(JSON.stringify(newDiscounts) !== JSON.stringify(originalDiscounts));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const idToken = await user.getIdToken();
      const res = await fetch(`${apiUrl}/api/v1/admin/airtime-discounts`, {
        method: 'POST',
        headers: { 
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(discounts)
      });
      if (res.ok) {
        setOriginalDiscounts(JSON.parse(JSON.stringify(discounts)));
        setHasChanges(false);
        alert("Airtime discounts updated successfully!");
      } else {
        alert("Failed to update airtime discounts");
      }
    } catch (error) {
      console.error("Error saving airtime discounts:", error);
      alert("Error saving airtime discounts");
    } finally {
      setSaving(false);
    }
  };

  const renderNetworkRow = (networkKey, displayName) => {
    const netData = discounts[networkKey];
    return (
      <tr key={networkKey} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
          {displayName}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <input 
              type="number" 
              step="0.1"
              min="0"
              max="100"
              className="w-20 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-2 py-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-center"
              value={netData.regular}
              onChange={(e) => handleChange(networkKey, 'regular', e.target.value)}
            />
            <span className="ml-1 text-gray-500">%</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <input 
              type="number" 
              step="0.1"
              min="0"
              max="100"
              className="w-20 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-2 py-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-center"
              value={netData.upgraded}
              onChange={(e) => handleChange(networkKey, 'upgraded', e.target.value)}
            />
            <span className="ml-1 text-gray-500">%</span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <input 
              type="number" 
              step="0.1"
              min="0"
              max="100"
              className="w-20 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md px-2 py-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-center"
              value={netData.api}
              onChange={(e) => handleChange(networkKey, 'api', e.target.value)}
            />
            <span className="ml-1 text-gray-500">%</span>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300">
      <Header onToggleMenuClick={toggleMenu} isOpen={isOpen} />
      <div className={`pt-16 px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        <main className="max-w-6xl mx-auto py-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
              Airtime Discounts
              {loading && <span className="ml-4 text-sm font-normal text-gray-500 animate-pulse">Loading...</span>}
            </h2>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className={`px-4 py-2 rounded-md font-medium text-white shadow-sm transition-colors ${saving || !hasChanges ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {!user ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 text-center">
                <p>Please log in to view this page.</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Set the percentage discount users receive when purchasing airtime. For example, a 2% discount on ₦100 airtime means the user pays ₦98.
                    </p>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Network
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Regular User Discount
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Reseller Discount
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    API User Discount
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                            {renderNetworkRow("MTN", "MTN")}
                            {renderNetworkRow("GLO", "GLO")}
                            {renderNetworkRow("AIRTEL", "Airtel")}
                            {renderNetworkRow("NINE_MOBILE", "9mobile")}
                        </tbody>
                    </table>
                </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AirtimePrices;
